import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';

import { ACCESSORIAL_DESCRIPTION } from 'doc-mate-store/lib/constants/accessorial';
import { useStores } from 'doc-mate-store/lib/hooks';
import { AdditionalBillingLeg } from 'doc-mate-store/lib/models/AdditionalBillingLeg';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { colors } from '../../themes';
import AdditionalBillingDropzoneSection from '../AdditionalBillingDocument/AdditionalBIllingDropzoneSection';
import ReorderableFileList from '../LoadDocument/ReorderableFileList';
import LoadDocumentPhotoList from '../LoadDocumentPhotoList';

const BILLING_TYPE_OPTIONS = Object.entries(ACCESSORIAL_DESCRIPTION)
  .filter(([key]) => key !== 'UNKNOWN')
  .map(([key, label]) => ({
    value: key,
    label: label,
  }));

type StagedFile = {
  id: string;
  file: File;
  isImage: boolean;
  previewUrl: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const AdditionalBillingBuilderDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const { updateAdditionalBillingId } = rootStore;

  const isEdit = !!updateAdditionalBillingId;

  const [manifestNumber, setManifestNumber] = useState<string>('');
  const [billingType, setBillingType] = useState<string>('BIF');
  const [notes, setNotes] = useState<string>('');

  const [existingLeg, setExistingLeg] = useState<AdditionalBillingLeg | null>(
    null,
  );
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

  const [fileError, setFileError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (updateAdditionalBillingId) {
      (async () => {
        setIsLoading(true);
        const res = await rootStore.fetchAdditionalBillingLeg(
          updateAdditionalBillingId,
        );
        if (res && res.ok && res.data) {
          const leg = res.data as AdditionalBillingLeg;
          setExistingLeg(leg);
          setManifestNumber(
            leg.baseAs400ManifestNumber || leg.as400ManifestNumber || '',
          );
          const loadedType = leg.billingType || 'BIF';
          const loadedNotes = leg.notes || '';
          setBillingType(loadedType);

          if (loadedType === 'DET') {
            setNotes(
              loadedNotes === 'Unloading' || loadedNotes === 'Loading'
                ? loadedNotes
                : 'Loading',
            );
          } else {
            setNotes(loadedNotes);
          }
        } else {
          setErrorMessage('Failed to load additional billing details.');
        }
        setIsLoading(false);
      })();
    }
  }, [updateAdditionalBillingId, rootStore]);

  const handleReset = useCallback(() => {
    setManifestNumber('');
    setBillingType('BIF');
    setNotes('');
    setExistingLeg(null);

    // Revoke object URLs to prevent memory leaks
    stagedFiles.forEach(sf => URL.revokeObjectURL(sf.previewUrl));
    setStagedFiles([]);

    setFileError(null);
    setErrorMessage(null);
    setIsLoading(false);
    rootStore.resetUpdateAdditionalBillingId?.();
  }, [rootStore, stagedFiles]);

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleBillingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedType = e.target.value;
    setBillingType(selectedType);

    if (selectedType === 'DET') {
      if (notes !== 'Loading' && notes !== 'Unloading') {
        setNotes('Loading');
      }
    }
  };

  const handleFilesUpload = useCallback((files: File[]) => {
    if (!files || files.length === 0) return;

    const newFiles: StagedFile[] = files.map(file => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      isImage: file.type.startsWith('image/'),
      previewUrl: URL.createObjectURL(file),
    }));

    setStagedFiles(prev => [...prev, ...newFiles]);
    setFileError(null);
  }, []);

  const handleDeleteStagedFile = useCallback((id: string) => {
    setStagedFiles(prev => {
      const target = prev.find(f => f.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const primaryDocument = existingLeg?.getDocument?.('additional_documents');

  // Track, sort, and format photos
  const existingPhotos = useMemo(() => {
    if (!primaryDocument?.photos) return [];
    return [...primaryDocument.photos]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(p => ({
        id: p.id,
        src: p.photo?.fullSize || '',
        thumbnail: p.photo?.thumbnail || '',
        width: p.width || 0,
        height: p.height || 0,
        rotate: p.rotate || 0,
        isStaged: false,
      }));
  }, [primaryDocument]);

  const stagedPhotos = useMemo(() => {
    return stagedFiles
      .filter(f => f.isImage)
      .map(f => ({
        id: f.id,
        src: f.previewUrl,
        thumbnail: f.previewUrl,
        width: 100,
        height: 100,
        rotate: 0,
        isStaged: true,
      }));
  }, [stagedFiles]);

  const allPhotos = useMemo(() => [...existingPhotos, ...stagedPhotos], [
    existingPhotos,
    stagedPhotos,
  ]);

  // Track, sort, and format files
  const existingFiles = useMemo(() => {
    if (!primaryDocument?.files) return [];
    return [...primaryDocument.files]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(f => ({
        id: f.id,
        name: f.name || f.file?.split('/').pop() || 'document.pdf',
        file: f.file,
        isStaged: false,
      }));
  }, [primaryDocument]);

  const stagedPdfs = useMemo(() => {
    return stagedFiles
      .filter(f => !f.isImage)
      .map(f => ({
        id: f.id,
        name: f.file.name,
        file: f.previewUrl,
        isStaged: true,
      }));
  }, [stagedFiles]);

  const allFiles = useMemo(() => [...existingFiles, ...stagedPdfs], [
    existingFiles,
    stagedPdfs,
  ]);

  const handleDeleteFile = useCallback(
    (fileId: number | string) => {
      const stagedTarget = stagedFiles.find(f => f.id === fileId);
      if (stagedTarget) {
        handleDeleteStagedFile(fileId as string);
        return;
      }

      if (!primaryDocument) return;

      confirmAlert({
        title: 'Confirm to Delete',
        message: 'Are you sure you want to delete this file?',
        buttons: [
          {
            label: 'Confirm',
            onClick: async () => {
              if (primaryDocument.deleteDocumentFile) {
                await primaryDocument.deleteDocumentFile(fileId);
              } else {
                await rootStore.deleteAdditionalBillingFile(
                  primaryDocument.id,
                  fileId,
                );
                primaryDocument.removeFile?.(fileId);
              }
            },
          },
          {
            label: 'Cancel',
          },
        ],
        overlayClassName: 'react-confirm-alert-overlay-custom',
      });
    },
    [handleDeleteStagedFile, primaryDocument, rootStore, stagedFiles],
  );

  const handleSubmit = useCallback(async () => {
    if (!manifestNumber) return;

    const totalAttachments = allPhotos.length + allFiles.length;
    if (totalAttachments === 0) {
      setFileError('At least one document or photo is required.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const payload = {
      manifestNumber,
      billingType,
      notes,
    };

    const result = isEdit
      ? await rootStore.updateAdditionalBillingLeg(
          updateAdditionalBillingId,
          payload,
        )
      : await rootStore.createAdditionalBillingLeg(payload);

    if (result && result.ok && result.data) {
      const leg = result.data as AdditionalBillingLeg;

      // Upload any new staged files
      if (stagedFiles.length > 0) {
        let doc = leg.getDocument?.('additional_documents');
        let docId = doc?.id;

        if (!docId) {
          const docResult = await rootStore.createAdditionalBillingDocument(
            leg.id,
            'additional_documents',
          );
          docId = docResult?.response?.entities?.id || docResult?.id;
        }

        if (docId) {
          for (const staged of stagedFiles) {
            const fileListMock = ({
              0: staged.file,
              length: 1,
            } as unknown) as FileList;

            if (staged.isImage) {
              await rootStore.addAdditionalBillingPhotos(docId, fileListMock);
            } else {
              await rootStore.addAdditionalBillingFiles(docId, fileListMock);
            }
          }
        }
      }

      setIsLoading(false);
      handleReset();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } else {
      setIsLoading(false);
      setErrorMessage(
        result?.errors?.error?.[0] ||
          `Failed to ${isEdit ? 'update' : 'create'} additional billing leg.`,
      );
    }
  }, [
    manifestNumber,
    billingType,
    notes,
    allPhotos.length,
    allFiles.length,
    stagedFiles,
    isEdit,
    updateAdditionalBillingId,
    rootStore,
    handleReset,
    onClose,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h3" className={classes.titleText}>
          {isEdit ? 'Edit Additional Billing' : 'Create Additional Billing'}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
        <DialogContent className={classes.dialogContent}>
          {isLoading && isEdit ? (
            <div className={classes.loadingWrap}>
              <CircularProgress size={32} />
            </div>
          ) : (
            <>
              {errorMessage && (
                <Typography className={classes.errorText} variant="body2">
                  {errorMessage}
                </Typography>
              )}

              <FormControl fullWidth className={classes.inputGroup}>
                <TextValidator
                  label="AS400 Manifest Number"
                  name="as400ManifestNumber"
                  value={manifestNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setManifestNumber(e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isLoading}
                  validators={['required']}
                  errorMessages={['This field is required']}
                />
              </FormControl>

              <FormControl fullWidth className={classes.inputGroup}>
                <TextValidator
                  select
                  label="Billing Type"
                  name="billingType"
                  value={billingType}
                  onChange={handleBillingTypeChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isLoading}
                  validators={['required']}
                  errorMessages={['This field is required']}
                >
                  {BILLING_TYPE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextValidator>
              </FormControl>

              {billingType === 'DET' ? (
                <FormControl
                  component="fieldset"
                  className={classes.inputGroup}
                >
                  <FormLabel component="legend" className={classes.radioLabel}>
                    Notes
                  </FormLabel>
                  <RadioGroup
                    aria-label="detention-notes"
                    name="detentionNotes"
                    value={notes || 'Loading'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNotes(e.target.value)
                    }
                    row
                  >
                    <FormControlLabel
                      value="Loading"
                      control={<Radio color="primary" size="small" />}
                      label="Loading"
                    />
                    <FormControlLabel
                      value="Unloading"
                      control={<Radio color="primary" size="small" />}
                      label="Unloading"
                    />
                  </RadioGroup>
                </FormControl>
              ) : (
                <FormControl fullWidth className={classes.inputGroup}>
                  <TextValidator
                    label="Notes"
                    name="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNotes(e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                    fullWidth
                    disabled={isLoading}
                  />
                </FormControl>
              )}

              {/* Attachments Section */}
              <div className={classes.documentSection}>
                <Typography variant="body2" className={classes.sectionLabel}>
                  ATTACHED DOCUMENTS *
                </Typography>

                <AdditionalBillingDropzoneSection
                  isDisabled={isLoading}
                  isUploading={isLoading}
                  onUpload={handleFilesUpload}
                >
                  {openFileDialog => (
                    <>
                      {/* PHOTOS */}
                      {allPhotos.length > 0 && (
                        <div className={classes.subSection}>
                          <Typography className={classes.documentText}>
                            PHOTOS:
                          </Typography>
                          <div className={classes.photoListWrapper}>
                            <LoadDocumentPhotoList
                              caption={`Additional Billing (${billingType})`}
                              photos={allPhotos}
                              onAdd={openFileDialog}
                              document={primaryDocument}
                              canUpdate={!isLoading}
                            />
                          </div>
                        </div>
                      )}

                      {/* FILES */}
                      {allFiles.length > 0 && (
                        <div className={classes.subSection}>
                          <Typography className={classes.documentText}>
                            DOCUMENTS:
                          </Typography>
                          <ReorderableFileList
                            files={allFiles.map(file => ({
                              id: file.id,
                              file: file.file,
                              name: file.name,
                            }))}
                            document={primaryDocument}
                            isEditable={!isLoading}
                            onDeleteConfirm={(_doc, fileId) =>
                              handleDeleteFile(fileId)
                            }
                          />
                        </div>
                      )}

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openFileDialog}
                        className={classes.addFileBtn}
                        disabled={isLoading}
                      >
                        Add Document / Photo
                      </Button>
                    </>
                  )}
                </AdditionalBillingDropzoneSection>

                {fileError && (
                  <Typography
                    variant="caption"
                    className={classes.fileErrorText}
                  >
                    {fileError}
                  </Typography>
                )}
              </div>
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isLoading}
            className={classes.submitBtn}
          >
            {isLoading
              ? isEdit
                ? 'Saving...'
                : 'Creating...'
              : isEdit
              ? 'Save'
              : 'Create'}
          </Button>
        </DialogActions>
      </ValidatorForm>
    </Dialog>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    backgroundColor: colors.textPrimary[400],
  },
  titleText: {
    fontWeight: 600,
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  inputGroup: {
    marginBottom: theme.spacing(2.5),
  },
  radioLabel: {
    fontSize: 12,
    marginBottom: theme.spacing(0.5),
  },
  errorText: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  documentSection: {
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  subSection: {
    marginBottom: theme.spacing(1.5),
  },
  documentText: {
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
  },
  photoListWrapper: {
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  addFileBtn: {
    textTransform: 'none',
    color: colors.primary[600],
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
  },
  fileErrorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    display: 'block',
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
  submitBtn: {
    backgroundColor: colors.primary[600],
    color: 'white',
    '&:hover': {
      backgroundColor: colors.primary[700],
    },
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
}));

export default observer(AdditionalBillingBuilderDialog);
